import { useCallback, useRef } from 'react'
import type { ConversationChat, ConversationCommand } from '@/api/conversation'
import { initialConversationState } from './state'
import type { ConversationController } from './controller'

/**
 * What starting a chat needs, injected for the same reason the panel's services are:
 * the draft is driven in tests without a network, and the create has to come from the
 * shell controller so its idempotency key and rail refresh are the ones that run.
 */
export type DraftConversationServices = {
  createChat: (projectId: number) => Promise<ConversationChat | undefined>
  // The command's acknowledgement is discarded: what it names is already in the chat
  // this draft is about to become, and the surface there reads it from the snapshot.
  submitCommand: (projectId: number, conversationId: number, command: ConversationCommand) => Promise<unknown>
}

/**
 * A chat that does not exist yet. It presents the same surface as a loaded one, with an
 * empty transcript and a live composer, and writes nothing until the first message: the
 * chat is created by that message rather than by the click that opened this. Abandoning
 * a draft therefore leaves nothing behind, which is the whole reason it exists.
 */
export function useDraftConversationController(
  projectId: number,
  services: DraftConversationServices,
  onStarted: (conversationId: number) => void
): ConversationController {
  const { createChat, submitCommand } = services
  const startedRef = useRef<number>(undefined)
  const creatingRef = useRef<Promise<ConversationChat | undefined>>(undefined)

  const start = useCallback(async (): Promise<number> => {
    // Two sends can overlap, and a send can fail after the chat was already created.
    // Both would otherwise leave a second empty chat behind, which is the thing this
    // whole surface exists to prevent, so the created id is remembered either way.
    if (startedRef.current !== undefined) return startedRef.current
    const pending = creatingRef.current ?? createChat(projectId)
    creatingRef.current = pending
    let created
    try {
      created = await pending
    } finally {
      if (creatingRef.current === pending) creatingRef.current = undefined
    }
    if (created === undefined) throw new Error('The chat could not be started. Try again.')
    startedRef.current = created.conversationId
    return created.conversationId
  }, [createChat, projectId])

  const submit = useCallback(
    async (command: ConversationCommand) => {
      const conversationId = await start()
      // Thrown rather than swallowed: the composer keeps the message and offers a retry,
      // and a draft that quietly dropped what was typed into it would be worse than one
      // that never opened.
      await submitCommand(projectId, conversationId, command)
      onStarted(conversationId)
    },
    [onStarted, projectId, start, submitCommand]
  )

  return {
    state: initialConversationState,
    status: 'ready',
    // Nothing is loaded, so there is nothing to reload, reconnect to, or page back
    // through. These are the honest implementations rather than placeholders.
    retry: () => undefined,
    reconnect: () => undefined,
    submit,
    loadOlder: () => Promise.resolve()
  }
}
